import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "../../context/AuthContext";
import { Icon } from "./icons";

/**
 * Kerangka ADMIN DASHBOARD (sidebar putih polos + aksen HITAM brand, section
 * Main/Others, latar #f2f2f2). Responsive: sidebar tetap di desktop, drawer di
 * mobile. Menu difilter berdasarkan role.
 */

const MANAJERIAL = ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "PIC"];
const HR = ["SUPER_ADMIN", "HR_ADMIN"];

const NAV = [
  { to: "/admin", label: "Ringkasan", icon: Icon.Home, end: true, section: "Main" },
  { to: "/admin/attendance", label: "Absensi", icon: Icon.Attendance, end: true, section: "Main", roles: MANAJERIAL },
  { to: "/admin/attendance/anomalies", label: "Monitor Anomali", icon: Icon.Alert, section: "Main", roles: MANAJERIAL },
  { to: "/admin/approval/leave", label: "Approval Cuti", icon: Icon.Leave, section: "Main", roles: MANAJERIAL },
  { to: "/admin/feedback", label: "Kotak Masuk", icon: Icon.Support, section: "Main", roles: HR },
  { to: "/admin/employees", label: "Karyawan", icon: Icon.Users, section: "Main", roles: HR },
  { to: "/admin/work-schedules", label: "Jadwal Kerja", icon: Icon.Clock, section: "Others", roles: HR },
  { to: "/admin/users", label: "Akun & Hierarki", icon: Icon.Key, section: "Others", roles: HR },
  { to: "/admin/roles", label: "Role", icon: Icon.Shield, section: "Others", roles: HR },
  { to: "/admin/audit", label: "Audit Log", icon: Icon.Doc, section: "Others", roles: HR },
  { to: "/admin/maintenance", label: "Maintenance", icon: Icon.Tools, section: "Others", roles: HR },
];

const SECTIONS = ["Main", "Others"];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visibleNav = NAV.filter((item) => !item.roles || hasRole(user, ...item.roles));

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const navItem = ({ isActive }) =>
    "relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition " +
    (isActive
      ? "bg-slate-100 text-slate-900 font-semibold before:absolute before:-left-3 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r-full before:bg-brand-600 [&>i]:text-brand-600"
      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900");

  return (
    <div className="h-screen overflow-hidden flex bg-appbg text-slate-800">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand */}
        <div className="h-16 flex items-center gap-3 px-5 shrink-0 border-b border-slate-100">
          <span className="h-9 w-9 rounded-full bg-brand-600 text-white flex items-center justify-center font-extrabold text-lg">S</span>
          <div className="leading-tight">
            <div className="font-bold text-slate-900">PJS HR</div>
            <div className="text-[11px] text-slate-500">Admin Dashboard</div>
          </div>
        </div>

        {/* Navigasi bersection */}
        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto no-scrollbar">
          {SECTIONS.map((sec) => {
            const items = visibleNav.filter((n) => n.section === sec);
            if (items.length === 0) return null;
            return (
              <div key={sec}>
                <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">{sec}</div>
                <div className="space-y-1">
                  {items.map((item) => {
                    const IconC = item.icon;
                    return (
                      <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setSidebarOpen(false)} className={navItem}>
                        <IconC size={18} />
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer: user + aksi */}
        <div className="p-3 space-y-1">
                    <button onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition">
            <Icon.Logout size={18} /> Portal Karyawan
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition">
            <Icon.Close size={18} /> Keluar
          </button>
          <div className="mt-2 pt-3 border-t border-slate-200 flex items-center gap-2.5 px-2">
            <div className="h-9 w-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center font-semibold uppercase shrink-0">
              {(user?.name || user?.username || "?")[0]}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || user?.username}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.role_name || user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Konten */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Bar mobile (hamburger) — desktop tanpa topbar, judul ada di page header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:hidden sticky top-0 z-10">
          <button className="text-slate-500" onClick={() => setSidebarOpen(true)}><Icon.Menu size={22} /></button>
          <span className="font-semibold text-slate-700">PJS HR Admin</span>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full w-full p-4 lg:p-5">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
